import React, { useMemo, useState } from 'react'
import { FormIcon, IFormIconProps, RenderHTML } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { toast } from '@reapit/elements/core/toaster'
import {
  AdminRecipientCard,
  AdminRecipientField,
  AdminRecipientStep,
  AdminReviewCard,
  FormFieldLinkOption,
  FullRecipientStep,
  RecipientFieldModel,
} from '../../../../api/types'
import { useAdminFormPreview } from '../../../../api/form-preview'
import { useAdminFieldLinks } from '../../../../api/field-links'
import { useBuilderDeletes } from '../../../../api/builder-deletes'
import { useAccessMode } from '../../../../access-mode-provider'
import { RecipientStepDrawer } from '../recipient-step-drawer'
import { RecipientCardDrawer } from '../recipient-card-drawer'
import { RecipientFieldDrawer } from '../recipient-field-drawer'
import { ConfirmDeleteDialog } from '../../../../components/confirm-delete-dialog'
import { AUTO_GENERATED_RECIPIENT_SECTION_TYPES } from '../recipient-section-types'
import {
  BuilderSplitLayout,
  Canvas,
  CardTile,
  EmptyState,
  LeftColumn,
  OutlineActions,
  OutlineAddCardLink,
  OutlineAddLink,
  OutlineCard,
  OutlineCardName,
  OutlineSection,
  OutlineSectionName,
  OutlineTree,
  SectionEditRow,
  SideCardHelp,
} from '../../style'
import {
  ActionSlot,
  AdditionalBlock,
  FieldLabel,
  FieldRow,
  HeadingBlock,
  HeadingValue,
  InlineRowActions,
  LiteralValue,
  RecipientCardBox,
  RecipientCardHeader,
  RecipientCardName,
  RegionCaption,
  RepeatBadge,
  SourceTag,
  StepBlock,
  StepCard,
  StepDescription,
  StepDivider,
  StepProgress,
  StepProgressCurrent,
  StepTitle,
  WizardColumn,
  WizardNav,
  FactoryCardCaption,
  FactoryEmptyNote,
} from './style'

interface RecipientViewProps {
  recipientSteps: FullRecipientStep[]
  reviewCards: AdminReviewCard[]
  formFieldOptions: FormFieldLinkOption[]
  formReferenceID: string
  versionReferenceID: string
  onChanged: () => void
}

const ACTION_LABELS: Record<string, string> = {
  Signing: 'Signature',
  SaveToContacts: 'Save to contacts',
  ViewInMaps: 'View in maps',
}

const scrollToStep = (referenceID: string) => {
  document.getElementById(`recipient-step-${referenceID}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/** The label shown beside a Main/Additional value. Recipient fields carry no title, so we use the display
 * prefix (the "Mobile:" style lead-in) when present. */
const fieldLabel = (field: AdminRecipientField): string => (field.displayPrefix ?? '').replace(/:\s*$/, '')

/** Lowercased for the placeholder tag, e.g. "currency", "dateTime" -> "date time". */
const formatTypeLabel = (formattingType: string): string =>
  formattingType.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()

/**
 * The value slot for a recipient field. The recipient sees real data; the builder has none, so we show where
 * the value comes from instead: a literal default/HTML block as-is, a merge token in a source tag, or —
 * when there's nothing configured yet — the field's formatting type, so an empty slot still tells you what
 * kind of value belongs there. Linked-form-field / not-linked states arrive with the linking ticket (FE-28).
 */
const FieldValue: React.FC<{ field: AdminRecipientField }> = ({ field }) => {
  if (field.actionType && field.actionType !== 'None') {
    return <ActionSlot>{ACTION_LABELS[field.actionType] ?? field.actionType} action</ActionSlot>
  }
  if (field.dataIsHtml && field.defaultData) {
    return <RenderHTML htmlString={field.defaultData} />
  }
  if (field.defaultDataHasMergeField && field.defaultData) {
    return <SourceTag>‹ {field.defaultData} ›</SourceTag>
  }
  if (field.defaultData) {
    return <LiteralValue>{field.defaultData}</LiteralValue>
  }
  const formattingHint =
    field.formattingType && field.formattingType !== 'None' ? formatTypeLabel(field.formattingType) : 'value'
  return <SourceTag>‹ {formattingHint} ›</SourceTag>
}

/**
 * Renders a factory-preview region (heading/body/additional). Fields with real data render individually; fields with
 * no data (always the case for the preview, since the factory runs against an empty placeholder recipient) are
 * collapsed into a single note instead of N identical `‹ value ›` rows.
 *
 * field.data is `unknown` (a string for most fields, but a Dictionary for "Save to Contacts" actions, a signature
 * object for signing fields). Use an explicit emptiness check so real `false`/`0` values aren't treated as empty.
 */
const hasFieldData = (data: unknown): boolean => data !== null && data !== undefined && data !== ''

const renderFactoryRegion = (
  fields: RecipientFieldModel[],
  Container: React.FC<{ children: React.ReactNode }>,
): React.ReactNode => {
  if (fields.length === 0) return null
  const populated = fields.filter((field) => field.isAction || hasFieldData(field.data))
  const emptyCount = fields.length - populated.length
  return (
    <>
      {populated.map((field, index) => (
        <Container key={`populated-${index}`}>
          <FactoryFieldValue field={field} />
        </Container>
      ))}
      {emptyCount > 0 && (
        <FactoryEmptyNote>
          {emptyCount} {emptyCount === 1 ? 'value' : 'values'} will populate here once the form has real recipient data
        </FactoryEmptyNote>
      )}
    </>
  )
}

const orderFields = (fields: AdminRecipientField[]): AdminRecipientField[] =>
  [...fields].sort((a, b) => a.displayIndex - b.displayIndex || a.displaySubIndex - b.displaySubIndex)

/** Repeatable card titles carry a literal `{{idx}}`/`{{index}}` placeholder for the instance number —
 * strip it here rather than showing the raw token in the heading. */
const cardTitle = (displayName: string): string => displayName.replace(/\s*\{\{\s*(idx|index)\s*\}\}/gi, '').trim()

/** The value slot for a component-factory field. Real recipient cards carry no per-field label — just a
 * pre-merged value (or an action affordance) — so we render it the same way real recipient data would show,
 * falling back to a generic placeholder since the factory ran against empty placeholder domain objects. */
const FactoryFieldValue: React.FC<{ field: RecipientFieldModel }> = ({ field }) => {
  if (field.isAction) {
    return <ActionSlot>{ACTION_LABELS[field.actionType ?? ''] ?? field.actionType ?? 'Action'}</ActionSlot>
  }
  if (field.isHtml && typeof field.data === 'string' && hasFieldData(field.data)) {
    return <RenderHTML htmlString={field.data} />
  }
  if (hasFieldData(field.data)) {
    return <LiteralValue>{String(field.data)}</LiteralValue>
  }
  return <SourceTag>‹ value ›</SourceTag>
}

/**
 * The Recipient App builder tab. Mirrors the recipient app's step wizard: a structure tree on the left
 * (steps → cards) and, on the right, each step rendered in a narrow centred column with its cards laid out
 * as the recipient sees them. Steps can be added/edited here; cards and fields arrive in later tickets.
 */
export const RecipientView: React.FC<RecipientViewProps> = ({
  recipientSteps,
  reviewCards,
  formFieldOptions,
  formReferenceID,
  versionReferenceID,
  onChanged,
}) => {
  const { data: preview, refresh: refreshPreview } = useAdminFormPreview(formReferenceID, versionReferenceID)
  const { data: fieldLinks, refresh: refreshFieldLinks } = useAdminFieldLinks(formReferenceID, versionReferenceID)
  const { isReadOnly } = useAccessMode()

  const previewByCard = useMemo(() => {
    const entries = preview?.recipientCards ?? []
    return new Map(entries.map((entry) => [entry.recipientCardReferenceID, entry]))
  }, [preview])

  const formFieldLabelByRef = useMemo(
    () => new Map(formFieldOptions.map((option) => [option.referenceID, option.label])),
    [formFieldOptions],
  )

  // Reverse lookup: which agent form field (if any) currently feeds a given recipient field.
  const linkedFormFieldByRecipientField = useMemo(() => {
    const map = new Map<string, string>()
    fieldLinks.forEach((link) => {
      if (link.recipientFieldReferenceID) map.set(link.recipientFieldReferenceID, link.formFieldReferenceID)
    })
    return map
  }, [fieldLinks])

  // A structural change (add/edit/delete) affects both the full-form graph and the factory preview, so refresh
  // both — otherwise a newly-added typed card shows the "no preview" fallback until a hard page reload.
  const handleChanged = () => {
    onChanged()
    refreshPreview()
    refreshFieldLinks()
  }

  /** A small chip naming the agent-side form field feeding this recipient field, or nothing if unlinked. The
   * full label carries its card/section breadcrumb (needed to disambiguate options in the Linked Form Field
   * select), but here we've already picked the field, so just its own name is enough. */
  const renderLinkBadge = (field: AdminRecipientField): React.ReactNode => {
    const linkedFormFieldRef = linkedFormFieldByRecipientField.get(field.referenceID)
    if (!linkedFormFieldRef) return null
    const fullLabel = formFieldLabelByRef.get(linkedFormFieldRef) ?? 'Linked field'
    const label = fullLabel.split(' · ').pop() ?? fullLabel
    return <SourceTag>🔗 {label}</SourceTag>
  }

  const steps = [...recipientSteps].sort((a, b) => a.step.order - b.step.order)
  const existingSteps = steps.map((entry) => entry.step)
  const nextStepOrder = existingSteps.reduce((max, step) => Math.max(max, step.order + 1), 1)

  const [isStepDrawerOpen, setIsStepDrawerOpen] = useState(false)
  const [editingStep, setEditingStep] = useState<AdminRecipientStep | null>(null)

  const openCreateStep = () => {
    setEditingStep(null)
    setIsStepDrawerOpen(true)
  }
  const openEditStep = (step: AdminRecipientStep) => {
    setEditingStep(step)
    setIsStepDrawerOpen(true)
  }

  const [isCardDrawerOpen, setIsCardDrawerOpen] = useState(false)
  const [activeStepRef, setActiveStepRef] = useState<string | null>(null)
  const [editingCard, setEditingCard] = useState<AdminRecipientCard | null>(null)

  const openCreateCard = (stepReferenceID: string) => {
    setActiveStepRef(stepReferenceID)
    setEditingCard(null)
    setIsCardDrawerOpen(true)
  }
  const openEditCard = (stepReferenceID: string, card: AdminRecipientCard) => {
    setActiveStepRef(stepReferenceID)
    setEditingCard(card)
    setIsCardDrawerOpen(true)
  }

  const [isFieldDrawerOpen, setIsFieldDrawerOpen] = useState(false)
  const [activeCardRef, setActiveCardRef] = useState<string | null>(null)
  const [editingField, setEditingField] = useState<AdminRecipientField | null>(null)

  const activeCardFields = useMemo(
    () => steps.flatMap((entry) => entry.cards).find((c) => c.card.referenceID === activeCardRef)?.fields ?? [],
    [steps, activeCardRef],
  )

  const openCreateField = (cardReferenceID: string) => {
    setActiveCardRef(cardReferenceID)
    setEditingField(null)
    setIsFieldDrawerOpen(true)
  }
  const openEditField = (cardReferenceID: string, field: AdminRecipientField) => {
    setActiveCardRef(cardReferenceID)
    setEditingField(field)
    setIsFieldDrawerOpen(true)
  }

  const { deleteRecipientStep, deleteRecipientCard, deleteRecipientField } = useBuilderDeletes()

  // A single confirm-before-delete dialog, reused for steps, cards and fields (mirrors the agent view).
  const [confirmDelete, setConfirmDelete] = useState<{
    title: string
    message: string
    run: () => Promise<void>
  } | null>(null)
  const [deleting, setDeleting] = useState(false)

  const runConfirmedDelete = async () => {
    if (!confirmDelete) return
    setDeleting(true)
    try {
      await confirmDelete.run()
      setConfirmDelete(null)
      handleChanged()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove this item.')
    } finally {
      setDeleting(false)
    }
  }

  const requestDeleteStep = (step: AdminRecipientStep) =>
    setConfirmDelete({
      title: 'Delete recipient step',
      message: `Delete "${step.displayName || 'this step'}"? This can't be undone.`,
      run: () => deleteRecipientStep(formReferenceID, versionReferenceID, step.referenceID),
    })

  const requestDeleteCard = (stepReferenceID: string, card: AdminRecipientCard) =>
    setConfirmDelete({
      title: 'Delete card',
      message: `Delete "${cardTitle(card.displayName) || 'this card'}"? This can't be undone.`,
      run: () => deleteRecipientCard(stepReferenceID, card.referenceID),
    })

  const requestDeleteField = (cardReferenceID: string, field: AdminRecipientField) =>
    setConfirmDelete({
      title: 'Delete field',
      message: `Delete "${fieldLabel(field) || 'this field'}"? This can't be undone.`,
      run: () => deleteRecipientField(cardReferenceID, field.referenceID),
    })

  return (
    <BuilderSplitLayout>
      <LeftColumn>
        <CardTile>
          <SideCardHelp>
            The recipient completes the form as a step-by-step wizard. Each step holds cards, and each card displays
            data collected on the agent side. The canvas previews how each step looks to the recipient.
          </SideCardHelp>
          {!isReadOnly && (
            <OutlineAddCardLink variant="tertiary" useLinkStyle type="button" onClick={openCreateStep}>
              + Add Recipient Step
            </OutlineAddCardLink>
          )}
          {steps.length > 0 && (
            <OutlineTree>
              {steps.map((entry) => (
                <OutlineCard key={entry.step.referenceID}>
                  <OutlineCardName
                    variant="tertiary"
                    useLinkStyle
                    type="button"
                    onClick={() => scrollToStep(entry.step.referenceID)}
                  >
                    {entry.step.displayName || 'Untitled step'}
                  </OutlineCardName>
                  {[...entry.cards]
                    .sort((a, b) => a.card.order - b.card.order)
                    .map((cardEntry) => (
                      <OutlineSection key={cardEntry.card.referenceID}>
                        <OutlineSectionName
                          variant="tertiary"
                          useLinkStyle
                          type="button"
                          onClick={() => scrollToStep(entry.step.referenceID)}
                        >
                          {cardTitle(cardEntry.card.displayName) || 'Untitled card'}
                        </OutlineSectionName>
                        {!isReadOnly && !AUTO_GENERATED_RECIPIENT_SECTION_TYPES.has(cardEntry.card.sectionType) && (
                          <OutlineActions>
                            <OutlineAddLink
                              variant="tertiary"
                              useLinkStyle
                              type="button"
                              onClick={() => openCreateField(cardEntry.card.referenceID)}
                            >
                              + Add Field
                            </OutlineAddLink>
                          </OutlineActions>
                        )}
                      </OutlineSection>
                    ))}
                  {!isReadOnly && (
                    <OutlineActions>
                      <OutlineAddLink
                        variant="tertiary"
                        useLinkStyle
                        type="button"
                        onClick={() => openCreateCard(entry.step.referenceID)}
                      >
                        + Add Card
                      </OutlineAddLink>
                    </OutlineActions>
                  )}
                </OutlineCard>
              ))}
            </OutlineTree>
          )}
        </CardTile>
      </LeftColumn>

      <Canvas>
        {steps.length === 0 ? (
          <EmptyState>No recipient steps yet. The recipient wizard is built from steps, cards and fields.</EmptyState>
        ) : (
          <WizardColumn>
            {steps.map((entry, index) => {
              const cards = [...entry.cards].sort((a, b) => a.card.order - b.card.order)
              return (
                <StepBlock key={entry.step.referenceID} id={`recipient-step-${entry.step.referenceID}`}>
                  {index > 0 && <StepDivider />}
                  <StepCard>
                    <WizardNav>
                      <span>Back</span>
                      <span>{entry.step.continueButtonText || 'Continue'}</span>
                    </WizardNav>
                    <div>
                      <StepProgress>
                        Step <StepProgressCurrent>{index + 1}</StepProgressCurrent> of {steps.length}
                      </StepProgress>
                      <StepTitle>{entry.step.displayName || 'Untitled step'}</StepTitle>
                      {entry.step.description && <StepDescription>{entry.step.description}</StepDescription>}
                    </div>

                    {cards.map((cardEntry) => {
                      const isTyped = AUTO_GENERATED_RECIPIENT_SECTION_TYPES.has(cardEntry.card.sectionType)
                      const factoryPreview = previewByCard.get(cardEntry.card.referenceID)

                      const heading = orderFields(cardEntry.fields.filter((field) => field.displayType === 'Heading'))
                      const additional = orderFields(
                        cardEntry.fields.filter((field) => field.displayType === 'Additional'),
                      )
                      // Everything else (Main, or an unset display type) renders in the main body.
                      const main = orderFields(
                        cardEntry.fields.filter(
                          (field) => field.displayType !== 'Heading' && field.displayType !== 'Additional',
                        ),
                      )
                      return (
                        <RecipientCardBox key={cardEntry.card.referenceID}>
                          <RecipientCardHeader>
                            {cardEntry.card.icon && (
                              <FormIcon
                                rounded
                                size="small"
                                intent="action"
                                iconName={cardEntry.card.icon as IFormIconProps['iconName']}
                              />
                            )}
                            <RecipientCardName>
                              {cardTitle(cardEntry.card.displayName) || 'Untitled card'}
                            </RecipientCardName>
                            {cardEntry.card.allowMultiple && <RepeatBadge>Repeats</RepeatBadge>}
                          </RecipientCardHeader>

                          {isTyped ? (
                            factoryPreview ? (
                              <>
                                <FactoryCardCaption>
                                  Generated by the {factoryPreview.sectionType} component · not individually editable
                                </FactoryCardCaption>
                                {renderFactoryRegion(factoryPreview.heading, HeadingValue)}
                                {renderFactoryRegion(factoryPreview.body, FieldRow)}
                                {factoryPreview.additional.length > 0 && (
                                  <AdditionalBlock>
                                    {renderFactoryRegion(factoryPreview.additional, FieldRow)}
                                  </AdditionalBlock>
                                )}
                              </>
                            ) : (
                              <FactoryCardCaption>
                                {cardEntry.card.sectionType} · fields generated automatically by this component
                              </FactoryCardCaption>
                            )
                          ) : (
                            <>
                              {heading.length > 0 && (
                                <HeadingBlock>
                                  <RegionCaption>Heading</RegionCaption>
                                  {heading.map((field) => (
                                    <HeadingValue key={field.referenceID}>
                                      <FieldValue field={field} />
                                      {renderLinkBadge(field)}
                                      {!isReadOnly && (
                                        <InlineRowActions>
                                          <Button
                                            variant="tertiary"
                                            useLinkStyle
                                            size="small"
                                            type="button"
                                            onClick={() => openEditField(cardEntry.card.referenceID, field)}
                                          >
                                            Edit
                                          </Button>
                                          <Button
                                            variant="tertiary"
                                            useLinkStyle
                                            isDestructive
                                            size="small"
                                            type="button"
                                            onClick={() => requestDeleteField(cardEntry.card.referenceID, field)}
                                          >
                                            Delete
                                          </Button>
                                        </InlineRowActions>
                                      )}
                                    </HeadingValue>
                                  ))}
                                </HeadingBlock>
                              )}

                              {main.length > 0 && (
                                <div>
                                  <RegionCaption>Main</RegionCaption>
                                  {main.map((field) => (
                                    <FieldRow key={field.referenceID}>
                                      {fieldLabel(field) && <FieldLabel>{fieldLabel(field)}</FieldLabel>}
                                      <FieldValue field={field} />
                                      {renderLinkBadge(field)}
                                      {!isReadOnly && (
                                        <InlineRowActions>
                                          <Button
                                            variant="tertiary"
                                            useLinkStyle
                                            size="small"
                                            type="button"
                                            onClick={() => openEditField(cardEntry.card.referenceID, field)}
                                          >
                                            Edit
                                          </Button>
                                          <Button
                                            variant="tertiary"
                                            useLinkStyle
                                            isDestructive
                                            size="small"
                                            type="button"
                                            onClick={() => requestDeleteField(cardEntry.card.referenceID, field)}
                                          >
                                            Delete
                                          </Button>
                                        </InlineRowActions>
                                      )}
                                    </FieldRow>
                                  ))}
                                </div>
                              )}
                            </>
                          )}

                          {additional.length > 0 && (
                            <div>
                              <RegionCaption>Additional</RegionCaption>
                              <AdditionalBlock>
                                {additional.map((field) => (
                                  <FieldRow key={field.referenceID}>
                                    {fieldLabel(field) && <FieldLabel>{fieldLabel(field)}</FieldLabel>}
                                    <FieldValue field={field} />
                                    {renderLinkBadge(field)}
                                    {!isReadOnly && (
                                      <InlineRowActions>
                                        <Button
                                          variant="tertiary"
                                          useLinkStyle
                                          size="small"
                                          type="button"
                                          onClick={() => openEditField(cardEntry.card.referenceID, field)}
                                        >
                                          Edit
                                        </Button>
                                        <Button
                                          variant="tertiary"
                                          useLinkStyle
                                          isDestructive
                                          size="small"
                                          type="button"
                                          onClick={() => requestDeleteField(cardEntry.card.referenceID, field)}
                                        >
                                          Delete
                                        </Button>
                                      </InlineRowActions>
                                    )}
                                  </FieldRow>
                                ))}
                              </AdditionalBlock>
                            </div>
                          )}

                          {!isReadOnly && (
                            <SectionEditRow>
                              <Button
                                variant="tertiary"
                                useLinkStyle
                                size="small"
                                type="button"
                                onClick={() => openEditCard(entry.step.referenceID, cardEntry.card)}
                              >
                                Edit
                              </Button>
                              {cardEntry.fields.length === 0 && (
                                <Button
                                  variant="tertiary"
                                  useLinkStyle
                                  isDestructive
                                  size="small"
                                  type="button"
                                  onClick={() => requestDeleteCard(entry.step.referenceID, cardEntry.card)}
                                >
                                  Delete
                                </Button>
                              )}
                            </SectionEditRow>
                          )}
                        </RecipientCardBox>
                      )
                    })}

                    {!isReadOnly && (
                      <SectionEditRow>
                        <Button
                          variant="tertiary"
                          useLinkStyle
                          size="small"
                          type="button"
                          onClick={() => openEditStep(entry.step)}
                        >
                          Edit
                        </Button>
                        {cards.length === 0 && (
                          <Button
                            variant="tertiary"
                            useLinkStyle
                            isDestructive
                            size="small"
                            type="button"
                            onClick={() => requestDeleteStep(entry.step)}
                          >
                            Delete
                          </Button>
                        )}
                      </SectionEditRow>
                    )}
                  </StepCard>
                </StepBlock>
              )
            })}
          </WizardColumn>
        )}
      </Canvas>

      <RecipientStepDrawer
        isOpen={isStepDrawerOpen}
        onOpenChange={setIsStepDrawerOpen}
        formReferenceID={formReferenceID}
        versionReferenceID={versionReferenceID}
        step={editingStep}
        existingSteps={existingSteps}
        nextOrder={nextStepOrder}
        reviewCards={reviewCards}
        onSaved={handleChanged}
      />
      <RecipientCardDrawer
        isOpen={isCardDrawerOpen}
        onOpenChange={setIsCardDrawerOpen}
        recipientStepReferenceID={activeStepRef ?? ''}
        card={editingCard}
        existingCards={steps.find((entry) => entry.step.referenceID === activeStepRef)?.cards.map((c) => c.card) ?? []}
        nextOrder={(steps.find((entry) => entry.step.referenceID === activeStepRef)?.cards ?? []).reduce(
          (max, cardEntry) => Math.max(max, cardEntry.card.order + 1),
          1,
        )}
        onSaved={handleChanged}
      />
      <RecipientFieldDrawer
        isOpen={isFieldDrawerOpen}
        onOpenChange={setIsFieldDrawerOpen}
        recipientCardReferenceID={activeCardRef ?? ''}
        field={editingField}
        existingFields={activeCardFields}
        nextDisplayIndex={activeCardFields
          .filter((existing) => existing.displayType === (editingField?.displayType || 'Main'))
          .reduce((max, existing) => Math.max(max, existing.displayIndex + 1), 1)}
        formFieldOptions={formFieldOptions}
        linkedFormFieldReferenceID={
          editingField ? (linkedFormFieldByRecipientField.get(editingField.referenceID) ?? null) : null
        }
        onSaved={handleChanged}
      />
      <ConfirmDeleteDialog
        isOpen={Boolean(confirmDelete)}
        title={confirmDelete?.title ?? ''}
        message={confirmDelete?.message ?? ''}
        busy={deleting}
        onConfirm={runConfirmedDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </BuilderSplitLayout>
  )
}
